// API Configuration
// Prefer explicit env; otherwise use current host so mobile/LAN works
const host = typeof window !== 'undefined' && window.location?.hostname ? window.location.hostname : 'localhost';
const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
const isPrivateIpv4Host = /^(10|127)\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)
  || /^192\.168\.\d{1,3}\.\d{1,3}$/.test(host)
  || /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(host);
const isPrivateLikeHost = isLocalHost || isPrivateIpv4Host || host.endsWith('.local');

let apiUrlEnv;
let publicWebUrlEnv;
try {
  // Try Vite way
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    apiUrlEnv = import.meta.env.VITE_API_URL || import.meta.env.REACT_APP_API_URL || import.meta.env.REACT_APP_API_BASE_URL;
    publicWebUrlEnv = import.meta.env.VITE_PUBLIC_WEB_URL || import.meta.env.REACT_APP_PUBLIC_WEB_URL || import.meta.env.REACT_APP_PUBLIC_APP_URL;
  }
} catch (e) { }

if (!apiUrlEnv) {
  // Try CRA/Node way with safety check
  try {
    if (typeof process !== 'undefined' && process.env) {
      apiUrlEnv = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL;
      publicWebUrlEnv = publicWebUrlEnv || process.env.REACT_APP_PUBLIC_WEB_URL || process.env.REACT_APP_PUBLIC_APP_URL;
    }
  } catch (e) { }
}

// Hosts that are known tunnel / expose services (same-origin API pattern)
const TUNNEL_HOST_SUFFIXES = [
  '.trycloudflare.com',
  '.cloudflareaccess.com',
  '.cfargotunnel.com',
  '.ngrok.io',
  '.ngrok-free.app',
  '.ngrok.app',
  '.loca.lt',
  '.serveo.net',
];
const isTunnelHost = TUNNEL_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));

const resolveDefaultApiBaseUrl = () => {
  if (isLocalHost) return `http://${host}:4003`;
  // Production and Render hosting subdomains use dedicated API subdomain/service.
  if (host === 'banabirtarifver.com' || host === 'www.banabirtarifver.com' || host.endsWith('.onrender.com')) {
    return 'https://api.banabirtarifver.com';
  }
  // Tunnel / expose services: frontend and backend share the same origin.
  // Returning empty string makes all /api/* calls go to the same host.
  if (isTunnelHost) return '';
  // For other custom domains, prefer same-origin.
  return origin || `https://${host}`;
};

export const API_BASE_URL = (apiUrlEnv || resolveDefaultApiBaseUrl()).replace(/\/+$/, '');

const resolvePublicWebOrigin = () => {
  if (publicWebUrlEnv) return String(publicWebUrlEnv).trim().replace(/\/+$/, '');
  if (host === 'banabirtarifver.com' || host === 'www.banabirtarifver.com') {
    return origin || 'https://banabirtarifver.com';
  }
  if (API_BASE_URL === 'https://api.banabirtarifver.com') {
    return 'https://banabirtarifver.com';
  }
  return (origin || '').replace(/\/+$/, '');
};

export const PUBLIC_WEB_ORIGIN = resolvePublicWebOrigin();
export const HAS_PUBLIC_SHARE_ORIGIN = Boolean(PUBLIC_WEB_ORIGIN) && !isPrivateLikeHost || API_BASE_URL === 'https://api.banabirtarifver.com';
export const CURRENT_ORIGIN_IS_PRIVATE = isPrivateLikeHost;

export const buildPublicShareUrl = (sharePath = '') => {
  const rawPath = String(sharePath || '').trim();
  if (!rawPath) return PUBLIC_WEB_ORIGIN || origin || '';
  if (/^https?:\/\//i.test(rawPath)) return rawPath;
  const base = PUBLIC_WEB_ORIGIN || origin || '';
  if (!base) return rawPath;
  return `${base}${rawPath.startsWith('/') ? rawPath : `/${rawPath}`}`;
};

const parseEnvBool = (value, defaultValue = false) => {
  if (value === undefined || value === null || value === '') return defaultValue;
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
  if (['0', 'false', 'no', 'off'].includes(normalized)) return false;
  return defaultValue;
};

export const ENABLE_LIBRARY_DETERMINISTIC_API = parseEnvBool(
  (typeof process !== 'undefined' && process.env)
    ? process.env.REACT_APP_ENABLE_LIBRARY_DETERMINISTIC_API
    : undefined,
  true
);

let recipeInsightModalEnv;
try {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    recipeInsightModalEnv = import.meta.env.VITE_RECIPE_INSIGHT_MODAL_V1 || import.meta.env.REACT_APP_RECIPE_INSIGHT_MODAL_V1;
  }
} catch (e) { }

if (recipeInsightModalEnv === undefined) {
  try {
    if (typeof process !== 'undefined' && process.env) {
      recipeInsightModalEnv = process.env.REACT_APP_RECIPE_INSIGHT_MODAL_V1;
    }
  } catch (e) { }
}

export const RECIPE_INSIGHT_MODAL_V1 = parseEnvBool(
  recipeInsightModalEnv,
  true
);

// Helper function for API calls
export const apiUrl = (endpoint) => {
  if (endpoint.startsWith('/')) {
    return `${API_BASE_URL}${endpoint}`;
  }
  return endpoint;
};
